import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// Phase 7 sub-bucket 19 — replace-file action for the admin content detail
// page. Wraps the existing three-step upload-init → direct PUT → PATCH chain
// (same as `useUploadContentAsset` but patches the existing row instead of
// creating a fresh asset).
//
// The previous storage object is orphaned in the bucket — content's storage
// cleanup runs lazily on row delete (legacy parity); a periodic sweep can
// reclaim those bytes later. Matches the deal-attachment carry-over pattern
// from sub-bucket 8.

type UploadInitResponse = {
  storageKey: string;
  token: string;
};

const CONTENT_BUCKET = "content";

function putToSignedUrl(signedUrl: string, file: File): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream",
    );
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new ApiClientError(
            xhr.status,
            "upload_failed",
            xhr.responseText || "Upload failed",
          ),
        );
      }
    });
    xhr.addEventListener("error", () =>
      reject(new ApiClientError(0, "upload_failed", "Network error during upload")),
    );
    xhr.addEventListener("abort", () =>
      reject(new ApiClientError(0, "upload_aborted", "Upload aborted")),
    );
    xhr.send(file);
  });
}

export type ReplaceContentAssetFileInput = {
  id: string;
  file: File;
};

export function useReplaceContentAssetFile() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ReplaceContentAssetFileInput>({
    mutationFn: async ({ id, file }) => {
      const init = await apiFetch<UploadInitResponse>(
        "/admin/content/upload-init",
        {
          method: "POST",
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type || undefined,
            size: file.size,
          }),
        },
      );

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new ApiClientError(0, "config_missing", "VITE_SUPABASE_URL not set");
      }
      const signedUrl = `${supabaseUrl}/storage/v1/object/upload/sign/${CONTENT_BUCKET}/${init.storageKey}?token=${init.token}`;
      await putToSignedUrl(signedUrl, file);

      return apiFetch<{ ok: true }>(`/admin/content/assets/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          file_url: `${CONTENT_BUCKET}/${init.storageKey}`,
        }),
      });
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.content.detail(id) });
      toast.success("File replaced");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to replace file");
    },
  });
}
